import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getIsVisible,
  getIsCollapsed,
  getContext,
  getMessages,
  getIsTyping,
  getPendingAction,
  getError
} from '../../../state/modules/aiAssistant/selectors';
import {
  toggleSidebarOperation,
  setSidebarCollapsedOperation,
  sendMessageOperation,
  executeActionOperation,
  clearMessagesOperation,
  cancelActionOperation
} from '../../../state/modules/aiAssistant/operations';
import ChatInterface from './ChatInterface';
import ContextBadge from './ContextBadge';
import CollapseToggle from './CollapseToggle';
import ActionPreview from './ActionPreview';
import SparkIcon from './SparkIcon';
import './index.scss';

const AIAssistantSidebar = ({
  isVisible,
  isCollapsed,
  context,
  messages,
  isTyping,
  pendingAction,
  error,
  onToggle,
  onCollapse,
  onSendMessage,
  onExecuteAction,
  onCancelAction,
  onClearMessages
}) => {
  const { pathname } = useLocation();
  const isOnAIPage = pathname.startsWith('/admin/ai-assistant');
  const isOnChatsPage = pathname.startsWith('/admin/chats');

  if (!isVisible || isOnAIPage || isOnChatsPage) {
    return null;
  }

  if (isCollapsed) {
    return <CollapseToggle onClick={onToggle} />;
  }

  return (
    <aside className="ai-assistant-sidebar" role="dialog" aria-label="AI Assistant">
      <div className="ai-assistant-sidebar__header">
        <span className="ai-assistant-sidebar__header-mark">
          <SparkIcon size={17} />
        </span>
        <div className="ai-assistant-sidebar__header-content">
          <h3 className="ai-assistant-sidebar__title">AI Assistant</h3>
          <ContextBadge context={context} />
        </div>
        <div className="ai-assistant-sidebar__header-actions">
          <button
            className="ai-assistant-sidebar__action-btn"
            onClick={() => onCollapse(true)}
            title="Close AI Assistant"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M5 5l14 14M19 5L5 19" />
            </svg>
          </button>
        </div>
      </div>

      <div className="ai-assistant-sidebar__body">
        {pendingAction && (
          <ActionPreview
            action={pendingAction}
            onExecute={() => onExecuteAction(pendingAction)}
            onCancel={onCancelAction}
          />
        )}

        <ChatInterface
          messages={messages}
          isTyping={isTyping}
          error={error}
          onSendMessage={onSendMessage}
        />
      </div>
    </aside>
  );
};

AIAssistantSidebar.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  context: PropTypes.shape({
    pageType: PropTypes.string,
    pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pageTitle: PropTypes.string,
    data: PropTypes.object
  }).isRequired,
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  isTyping: PropTypes.bool.isRequired,
  pendingAction: PropTypes.object,
  error: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onExecuteAction: PropTypes.func.isRequired,
  onCancelAction: PropTypes.func.isRequired,
  onClearMessages: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  isVisible: getIsVisible(state),
  isCollapsed: getIsCollapsed(state),
  context: getContext(state),
  messages: getMessages(state),
  isTyping: getIsTyping(state),
  pendingAction: getPendingAction(state),
  error: getError(state)
});

const mapDispatchToProps = (dispatch) => ({
  onToggle: () => dispatch(toggleSidebarOperation()),
  onCollapse: (collapsed) => dispatch(setSidebarCollapsedOperation(collapsed)),
  onSendMessage: (message) => dispatch(sendMessageOperation(message)),
  onExecuteAction: (action) => dispatch(executeActionOperation(action)),
  onCancelAction: () => dispatch(cancelActionOperation()),
  onClearMessages: () => dispatch(clearMessagesOperation())
});

export default connect(mapStateToProps, mapDispatchToProps)(AIAssistantSidebar);
